import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Star, DollarSign, Zap, CheckCircle, Shield, Wrench, MessageCircle, Scale, RefreshCcw, ArrowRight, Activity, Calendar, BarChart3, TrendingUp, FileCheck, Handshake, Trophy, GraduationCap, Search, Clock, Layers, Plus, Target, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "@/utils/constants";
import { SEO, JsonLd } from "@/components/common/SEO";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { AccountSizeCards } from "@/components/firms/AccountSizeCards";
import { FirmList } from "@/components/firms/FirmCardRedesign";
import { CompareModal } from "@/components/firms/CompareModal";
import { CompareBar } from "@/components/firms/CompareBar";
import { LiveActivityFeed } from "@/components/marketing/LiveActivityFeed";
import { LiveDealsStrip } from "@/components/marketing/LiveDealsStrip";
import { TrustedEcosystemSection } from "@/components/home/TrustedEcosystemSection";
import { getSizeNumeric } from "@/utils/sizeUtils";
import { parsePrice } from "@/utils/priceUtils";

const BENTO_CARDS = [
  {
    icon: "\u{1F4B0}", title: "Why Traders Use Futures Prop Firms for Funded Accounts", teaser: "Trade real capital without risking your own money.",
    body: (<>
      <p>A funded futures account lets you trade real capital without risking your own money. Prop firms provide the buying power — you provide the skill. Pass an evaluation, meet their drawdown and profit targets, and you trade a live account with a profit split that typically ranges from 80% to 100%.</p>
      <p>But not every firm structures their rules the same way. Some use <Link to="/trailing-drawdown-prop-firms" className="text-teal-700 dark:text-teal-400 hover:underline">trailing drawdown</Link> that follows your equity high. Others use <Link to="/eod-drawdown-prop-firms" className="text-teal-700 dark:text-teal-400 hover:underline">end-of-day (EOD) drawdown</Link> that only updates at session close. Some require consistency rules or minimum trading days. Others let you trade your way from day one.</p>
      <p>These differences directly impact whether you pass or fail an evaluation — and how much you actually keep once funded. That's why a side-by-side comparison matters more than any single review.</p>
    </>),
    pills: [{ label: "Trailing drawdown firms", to: "/trailing-drawdown-prop-firms" }, { label: "EOD drawdown firms", to: "/eod-drawdown-prop-firms" }],
  },
  {
    icon: "\u{1F4CA}", title: "How FuturesFury Compares Futures Prop Firms", teaser: "10 firms tracked across every metric that matters.",
    body: (<>
      <p>FuturesFury tracks 10 futures prop firms across every metric that affects your bottom line: evaluation cost, drawdown type, profit split, payout speed, reset fees, and trading restrictions. Every data point is verified against each firm's current rules and updated monthly.</p>
      <p>Start with the <Link to="/futures-prop-firm-comparison" className="text-teal-700 dark:text-teal-400 hover:underline">full comparison table</Link> to see all 10 firms ranked by price, rules, and payout structure. If cost is your priority, the <Link to="/cheapest-futures-prop-firms" className="text-teal-700 dark:text-teal-400 hover:underline">cheapest futures prop firms</Link> breakdown shows true cost-to-funding after discounts and resets. Looking for speed? The <Link to="/prop-firms-fast-payouts" className="text-teal-700 dark:text-teal-400 hover:underline">fastest payout firms</Link> ranking shows which firms get your profits to you in days, not weeks.</p>
      <p>Every firm page includes a detailed breakdown of rules, <Link to="/futures-prop-firm-discounts" className="text-teal-700 dark:text-teal-400 hover:underline">verified discount codes</Link>, and a direct comparison against similar firms — so you can see exactly where each one stands before you pay for an evaluation.</p>
    </>),
    pills: [{ label: "Comparison table", to: "/futures-prop-firm-comparison" }, { label: "Cheapest firms", to: "/cheapest-futures-prop-firms" }, { label: "Fastest payouts", to: "/prop-firms-fast-payouts" }, { label: "Discount codes", to: "/futures-prop-firm-discounts" }],
  },
  {
    icon: "\u{1F4CB}", title: "Understanding Prop Firm Rules Before You Trade", teaser: "Most traders fail on rules, not strategy.",
    body: (<>
      <p>Most traders don't fail evaluations because their strategy is bad. They fail because they didn't fully understand the rules before they started. Trailing drawdown behaves differently from static drawdown. A daily loss limit is not the same as a max drawdown. And a consistency rule can disqualify a profitable account.</p>
      <p>The <Link to="/futures-prop-firm-rules" className="text-teal-700 dark:text-teal-400 hover:underline">prop firm rules guide</Link> breaks down every rule type across all 10 firms: drawdown mechanics, profit targets, daily limits, scaling plans, news trading restrictions, and payout conditions. For a visual breakdown of how drawdown types affect your equity curve, use the <Link to="/tools" className="text-teal-700 dark:text-teal-400 hover:underline">free Drawdown Visualizer tool</Link> to simulate real scenarios before committing to an evaluation.</p>
      <p>New to prop trading? The <Link to="/best-futures-prop-firms-for-beginners" className="text-teal-700 dark:text-teal-400 hover:underline">beginner's guide to futures prop firms</Link> walks through what to look for in your first evaluation, which firms are most forgiving on rules, and how to avoid the most common mistakes that blow accounts in the first week.</p>
    </>),
    pills: [{ label: "Rules guide", to: "/futures-prop-firm-rules" }, { label: "Drawdown Visualizer", to: "/tools" }, { label: "Beginner's guide", to: "/best-futures-prop-firms-for-beginners" }],
  },
  {
    icon: "\u{1F3AF}", title: "Find the Right Futures Prop Firm for Your Strategy", teaser: "Filter firms by how you actually trade.",
    body: (<>
      <p>There is no single best prop firm. The right choice depends on how you trade. Scalpers need firms without consistency rules and with tight trailing drawdown buffers. Swing traders want EOD drawdown and no daily loss limits. High-frequency traders need platforms with fast execution and no restrictions on order flow tools.</p>
      <p>Use the comparison tools to filter by what matters to your approach: <Link to="/news-trading-prop-firms" className="text-teal-700 dark:text-teal-400 hover:underline">firms that allow news trading</Link>, <Link to="/prop-firms-without-consistency-rule" className="text-teal-700 dark:text-teal-400 hover:underline">firms with no consistency rule</Link>, or <Link to="/lowest-reset-fee-prop-firms" className="text-teal-700 dark:text-teal-400 hover:underline">firms with the lowest reset fees</Link>. Every filter page shows the firms that match, ranked by overall value.</p>
    </>),
    pills: [{ label: "News trading firms", to: "/news-trading-prop-firms" }, { label: "No consistency rule", to: "/prop-firms-without-consistency-rule" }, { label: "Lowest reset fees", to: "/lowest-reset-fee-prop-firms" }],
  },
];

const HomePage = () => {
  const [firms, setFirms] = useState([]);
  const [selectedSize, setSelectedSize] = useState(50000);
  const [sortBy, setSortBy] = useState("top-rated");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [expandedBento, setExpandedBento] = useState(null);
  const firmListRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    const timestamp = Date.now();
    const fetchFirms = async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const res = await axios.get(`${API}/firms`, {
            params: { _t: timestamp },
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
          });
          if (res.data?.length) {
            setFirms(res.data);
            return;
          }
        } catch (err) {
          if (attempt < 3 && (err.response?.status >= 500 || !err.response)) {
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
            continue;
          }
        }
      }
      // All retries failed
      setFetchError(true);
    };
    fetchFirms().finally(() => setLoading(false));
  }, []);

  // When a size is selected: show only firms with that size, sorted by price asc
  // selectedSize is now numeric (e.g. 50000) — all comparisons use size_numeric
  const sortedFirms = (() => {
    if (!selectedSize) {
      return [...firms].sort((a, b) => {
        if (sortBy === "fastest") return (a.min_days || 99) - (b.min_days || 99);
        return (b.verified_trader_rating || b.rating || 0) - (a.verified_trader_rating || a.rating || 0);
      });
    }
    const withSize = firms.filter(f => f.pricing?.some(p => getSizeNumeric(p) === selectedSize));
    const sorted = [...withSize].sort((a, b) => {
      const aPrice = parsePrice(a.pricing?.find(p => getSizeNumeric(p) === selectedSize)?.price);
      const bPrice = parsePrice(b.pricing?.find(p => getSizeNumeric(p) === selectedSize)?.price);
      return aPrice - bPrice;
    });
    return sorted;
  })();

  const toggleCompare = (firmId) => {
    setCompareIds(prev =>
      prev.includes(firmId) ? prev.filter(id => id !== firmId) : prev.length < 3 ? [...prev, firmId] : prev
    );
  };
  const compareFirms = firms.filter(f => compareIds.includes(f.id));

  const scrollToFirms = () => {
    firmListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What is a futures prop firm?", "acceptedAnswer": { "@type": "Answer", "text": "A proprietary (prop) trading firm provides traders with capital to trade futures markets. Traders pass an evaluation, then trade with firm capital and keep a percentage of profits." }},
      { "@type": "Question", "name": "How do prop firm evaluations work?", "acceptedAnswer": { "@type": "Answer", "text": "Traders pay an evaluation fee and must hit profit targets while staying within drawdown limits. Once passed, traders receive a funded account with the firm's capital." }},
      { "@type": "Question", "name": "What is trailing drawdown?", "acceptedAnswer": { "@type": "Answer", "text": "Trailing drawdown tracks your account's highest equity point and sets the max loss relative to that peak. It's stricter than static drawdown because unrealized gains can tighten your buffer." }},
    ]
  };

  return (
    <div className="min-h-screen surface-page">
      <SEO path="/"
       
       
        canonical="/"
        breadcrumbs={[{name: "Home", path: "/"}]}
      />
      <JsonLd data={faqSchema} />
      <Header />

      {/* Live Activity Feed */}
      <LiveActivityFeed />

      {/* ══════════════ A) HERO — UNCHANGED ══════════════ */}
      <section className="pt-32 pb-6 sm:pb-8 px-4 sm:px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent" />
        <div className="relative max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-3 sm:mb-4 tracking-tight">
            Best Futures Prop Firms (2026) — <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-400">Compare</span> & Choose the Right Firm
          </h1>
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 mb-4 sm:mb-6 max-w-2xl mx-auto leading-relaxed">
            Transparent futures prop firm comparisons, real rules, and free tools to help futures traders make informed decisions. <Link to="/futures-prop-firm-discounts" className="text-blue-500 hover:underline">Explore verified discounts</Link> or <Link to="/tools" className="text-blue-500 hover:underline">try our free tools</Link>.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={scrollToFirms}
              className="bg-blue-500 hover:bg-[#1565C0] text-white px-6 py-3 rounded-xl font-semibold transition-colors"
              data-testid="hero-cta-compare"
            >
              Compare Futures Prop Firms
            </button>
            <Link
              to="/tools"
              className="border border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-500 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-6 py-3 rounded-xl font-medium transition-colors"
              data-testid="hero-cta-tools"
            >
              Explore Free Trading Tools
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════ B) WHY WE FEATURE 10 — MOVED UP ══════════════ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="why-we-feature-10">
        <div className="rounded-2xl border border-slate-200/70 dark:border-white/[0.07] bg-white dark:bg-[#0c1019] shadow-[0_2px_20px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_24px_rgba(0,0,0,0.3)] p-6 sm:p-8">
          <p className="text-[10px] tracking-widest font-semibold uppercase text-emerald-600 dark:text-emerald-400 mb-3">Curated Top 10</p>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
            Why We Feature <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-400">10</span> Futures Prop Firms — Not Hundreds
          </h2>
          <div className="text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-5 space-y-2">
            <p className="font-medium text-slate-800 dark:text-slate-200">You don't need dozens of options to make a smart decision.</p>
            <p>Too many options create noise. Our goal is clarity. Instead of volume, we focus on a curated selection of ten — a balanced mix of established operators and promising new firms. We evaluate each firm based on <Link to="/trailing-drawdown-prop-firms" className="text-blue-500 hover:underline">how trailing drawdown works</Link> and which are the <Link to="/cheapest-futures-prop-firms" className="text-blue-500 hover:underline">cheapest futures prop firms</Link>.</p>
          </div>

          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Each featured firm is reviewed for:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            {[
              { icon: FileCheck, label: "Transparent rule structure" },
              { icon: Shield, label: "Clear drawdown mechanics" },
              { icon: DollarSign, label: "Payout reliability" },
              { icon: Handshake, label: "Trader alignment" },
            ].map(tile => (
              <div key={tile.label} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 rounded-lg px-3 py-2.5 transition-all duration-150 hover:shadow-sm hover:-translate-y-px">
                <tile.icon size={14} className="text-blue-500 flex-shrink-0" />
                <span className="text-xs text-slate-700 dark:text-slate-300">{tile.label}</span>
              </div>
            ))}
          </div>

          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Clarity beats quantity.</p>
          <p className="text-xs text-slate-500 dark:text-slate-300 mb-4 max-w-md mx-auto">From scalp-friendly firms like <Link to="/firms/yrm-prop" className="text-blue-500 hover:underline">YRM Prop</Link> to established names like <Link to="/firms/top-one-futures" className="text-blue-500 hover:underline">Top One Futures</Link> — every firm earns its place through data, not partnerships.</p>
          <Link
            to="/how-we-rank-prop-firms"
            className="inline-flex items-center gap-2 border border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white rounded-xl px-5 py-2.5 text-sm font-medium transition-all duration-200"
            data-testid="curated-cta"
          >
            See How We Select Firms <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* ══════════════ B.5) LIVE DEALS STRIP ══════════════ */}
      <LiveDealsStrip firms={firms} />

      {/* ══════════════ C) ACCOUNT SIZE + 10 FIRM GRID — UNTOUCHED ══════════════ */}
      <AccountSizeCards selected={selectedSize} onSelect={setSelectedSize} firms={firms} />

      <main id="main-content" tabIndex={-1} ref={firmListRef} className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div className="flex gap-1 sm:gap-2 overflow-x-auto">
            {[
              { id: "top-rated", label: "Top Rated", icon: Star },
              { id: "lowest-cost", label: "Lowest Cost", icon: DollarSign },
              { id: "fastest", label: "Fastest Fund", icon: Zap }
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => setSortBy(filter.id)}
                data-testid={`sort-${filter.id}`}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  sortBy === filter.id ? "bg-slate-100 dark:bg-white/10 text-slate-900 dark:text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/5"
                }`}
              >
                <filter.icon size={14} /> {filter.label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fetchError && firms.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="firms-error">
            <p className="text-lg mb-2">Unable to load firm data</p>
            <p className="text-sm mb-4">Check your connection and try again</p>
            <button onClick={() => window.location.reload()} className="text-sm text-blue-500 hover:underline">Refresh page</button>
          </div>
        ) : sortedFirms.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="firms-empty-filter">
            <p className="text-lg mb-2">No firms available{selectedSize ? ` for ${selectedSize}` : ""}</p>
            <p className="text-sm">Try selecting a different account size</p>
          </div>
        ) : (
          <FirmList firms={sortedFirms} selectedSize={selectedSize} />
        )}
      </main>

      <CompareBar
        count={compareIds.length}
        firms={compareFirms}
        onClear={() => setCompareIds([])}
        onCompare={() => setShowCompare(true)}
        onRemove={(id) => setCompareIds(prev => prev.filter(i => i !== id))}
      />
      {showCompare && compareFirms.length >= 2 && (
        <CompareModal firms={compareFirms} selectedSize={selectedSize} onClose={() => setShowCompare(false)} />
      )}

      {/* ══════════════ D) COMPARE. UNDERSTAND. EXECUTE. — UNIFIED ══════════════ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="compare-understand-execute">
        <div className="rounded-2xl border border-slate-200/70 dark:border-white/[0.07] bg-white dark:bg-[#0c1019] shadow-[0_2px_20px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_24px_rgba(0,0,0,0.35)] p-5 sm:p-7">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-1 tracking-tight">Compare. Understand. Execute.</h2>
          <p className="text-sm text-slate-500 dark:text-slate-300 mb-6">Compare firms, understand rules, and access trader tools — all in one place. See how firms like <Link to="/firms/blue-guardian" className="text-blue-500 hover:underline">Blue Guardian</Link> differ in their approach to drawdown and payouts.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Column 1 — Compare */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3 pb-2 border-b border-slate-200/60 dark:border-white/[0.06]">Compare</p>
              <div className="space-y-3">
                {[
                  { to: "/futures-prop-firm-comparison", icon: Trophy, iconColor: "text-amber-500", title: "Compare top futures prop firms", desc: "Best overall across cost, rules, and payouts" },
                  { to: "/best-prop-firms-for-scalping", icon: Zap, iconColor: "text-yellow-500", title: "Best prop firms for scalping", desc: "Fast execution, low hold time" },
                  { to: "/best-prop-firms-under-100", icon: DollarSign, iconColor: "text-emerald-500", title: "Cheapest prop firms", desc: "Lowest total cost to funded" },
                  { to: "/prop-firms-fast-payouts", icon: Clock, iconColor: "text-sky-500", title: "Fast payout prop firms", desc: "Get paid in as little as ~5\u201310 days" },
                  { to: "/prop-firms-without-activation-fee", icon: Tag, iconColor: "text-violet-500", title: "No activation fee prop firms", desc: "Start without extra costs" },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="group flex items-start gap-3 py-1.5" data-testid={`cue-${link.to.slice(1)}`}>
                    <link.icon size={15} className={`${link.iconColor} flex-shrink-0 mt-0.5`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-white/80 group-hover:text-blue-500 transition-colors">{link.title}</p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-snug">{link.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Column 2 — Rules & Models */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3 pb-2 border-b border-slate-200/60 dark:border-white/[0.06]">Rules &amp; Models</p>
              <div className="space-y-3">
                {[
                  { to: "/trailing-drawdown-prop-firms", icon: TrendingUp, iconColor: "text-emerald-500", title: "Trailing drawdown firms explained", desc: "How it works + best firms" },
                  { to: "/static-drawdown-prop-firms", icon: Shield, iconColor: "text-teal-500", title: "Static drawdown firms explained", desc: "Which model fits your style" },
                  { to: "/best-futures-prop-firms-for-beginners", icon: GraduationCap, iconColor: "text-sky-500", title: "Best prop firms for beginners", desc: "Forgiving rules for new traders" },
                  { to: "/drawdown-types", icon: Layers, iconColor: "text-violet-500", title: "Drawdown Types", desc: "EOD vs intraday vs hybrid" },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="group flex items-start gap-3 py-1.5" data-testid={`cue-${link.to.slice(1)}`}>
                    <link.icon size={15} className={`${link.iconColor} flex-shrink-0 mt-0.5`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-white/80 group-hover:text-blue-500 transition-colors">{link.title}</p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-snug">{link.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Column 3 — Tools & Insights */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3 pb-2 border-b border-slate-200/60 dark:border-white/[0.06]">Tools &amp; Insights</p>
              <div className="space-y-3 mb-5">
                {[
                  { to: "/tools#calculators", icon: Clock, iconColor: "text-blue-500", title: "Payout Timeline Estimator", desc: "See your first payout date" },
                  { to: "/tools#market-movers", icon: Zap, iconColor: "text-amber-500", title: "Market Movers", desc: "High-impact events + volatility" },
                  { to: "/tools#vix-gauge", icon: Activity, iconColor: "text-rose-500", title: "VIX Gauge", desc: "Volatility state + sizing cue" },
                  { to: "/do-not-trade", icon: Target, iconColor: "text-amber-400", title: "Do Not Trade Simulator", desc: "Test trading discipline" },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="group flex items-start gap-3 py-1.5" data-testid={`cue-${link.to.replace(/[#/]/g, '-').slice(1)}`}>
                    <link.icon size={15} className={`${link.iconColor} flex-shrink-0 mt-0.5`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-white/80 group-hover:text-blue-500 transition-colors">{link.title}</p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-snug">{link.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
              {/* CTAs */}
              <Link to="/tools" className="block text-center bg-gradient-to-r from-blue-500 to-blue-500 hover:from-[#1565C0] hover:to-[#1976D2] text-white rounded-xl px-5 py-2.5 text-sm font-semibold shadow-md shadow-blue-500/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/30 mb-2.5" data-testid="cue-tools-cta">
                Explore Free Trading Tools
              </Link>
              <Link to="/futures-prop-firm-comparison" className="block text-center text-xs text-blue-500 hover:underline font-medium py-1" data-testid="cue-compare-cta">
                Compare Firms
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ D.5) DO NOT TRADE SIMULATOR CALLOUT ══════════════ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-4" data-testid="simulator-callout">
        <Link to="/do-not-trade" className="group block rounded-2xl border border-amber-500/15 hover:border-amber-500/30 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5 dark:from-amber-500/[0.03] dark:via-transparent dark:to-amber-500/[0.03] p-5 sm:p-6 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,193,7,0.04)]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Target size={18} className="text-amber-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">New Tool</span>
                </div>
                <p className="text-base font-bold text-slate-900 dark:text-white">Do Not Trade Simulator</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Test your trading discipline before risking real money.</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-500 dark:text-amber-400 group-hover:gap-2.5 transition-all whitespace-nowrap">
              Play Now <ArrowRight size={14} />
            </span>
          </div>
        </Link>
      </section>

      {/* ══════════════ E) FREE TOOLS SECTION — PRESERVED ══════════════ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="free-tools-showcase">
        <div className="bg-gradient-to-br from-blue-50/80 via-white to-emerald-50/60 dark:from-slate-900 dark:via-[#0D1220] dark:to-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
            <Shield size={10} /> 100% Free
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
            Free Tools. Built for Traders <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-500">Like You.</span>
          </h2>
          <p className="text-slate-500 dark:text-slate-300 text-sm mb-6 max-w-lg">
            Professional-grade tools — not just for institutions.
          </p>
          <div className="space-y-2.5 mb-6">
            {[
              { icon: Activity, title: "Live VIX Gauge", tag: "LIVE", color: "#E53935", desc: "Real-time market fear index before you enter a trade." },
              { icon: Calendar, title: "Economic Calendar", tag: "DAILY", color: "#3b82f6", desc: "Fed, CPI, NFP — filtered for what moves futures." },
              { icon: Zap, title: "Market Movers", tag: "DAILY", color: "#FF6D00", desc: "Live economic releases + real-time macro & social headline risk — built for futures traders.", subdesc: "Updated automatically during active market sessions." },
              { icon: BarChart3, title: "Drawdown & Payout Calculators", tag: "INTERACTIVE", color: "#00C853", desc: "Visualize trailing vs EOD drawdown and estimate first payout." },
              { icon: TrendingUp, title: "Profit Tracker", tag: "FREE", color: "#AB47BC", desc: "Log every eval fee, reset, and payout. Export CPA-ready PDFs." },
            ].map(tool => (
              <div key={tool.title} className="flex items-start gap-3 bg-white/60 dark:bg-[#0A0E17]/60 border border-slate-100 dark:border-[#1A2133] rounded-xl p-3 transition-colors">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${tool.color}12` }}>
                  <tool.icon size={15} style={{ color: tool.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">{tool.title}</span>
                    <span className="text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full" style={{ background: `${tool.color}12`, color: tool.color }}>{tool.tag}</span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-500 leading-relaxed">{tool.desc}</p>
                  {tool.subdesc && <p className="text-[11px] text-slate-400/70 dark:text-slate-600 leading-relaxed mt-1">{tool.subdesc}</p>}
                </div>
              </div>
            ))}
          </div>
          <Link to="/tools" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-[#1565C0] hover:to-emerald-600 text-white rounded-xl px-5 py-2.5 text-sm font-medium shadow-md transition-all duration-200 hover:-translate-y-0.5" data-testid="tools-cta">
            Explore Free Trading Tools <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* ══════════════ Trusted Futures Trading Ecosystem ══════════════ */}
      <TrustedEcosystemSection />

      {/* ══════════════ F) SEO BENTO GRID — What Makes Futures Prop Firms Different ══════════════ */}
      <section className="py-20 px-4 sm:px-6 bg-slate-50 dark:bg-[#0b0d14]" data-testid="seo-bento-grid">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-500 mb-3 block">Learn More</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">What Makes Futures Prop Firms Different</h2>
            <p className="text-slate-500 dark:text-slate-400 text-base">Understand the rules before you risk an evaluation fee.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {BENTO_CARDS.map((card, i) => {
              const isOpen = expandedBento === i;
              return (
                <div key={i} className={`rounded-xl border transition-all duration-300 ${isOpen ? "sm:col-span-2 border-teal-600 bg-teal-50 dark:bg-[#1a1e2c]" : "border-slate-200 dark:border-[#1c2035] bg-white dark:bg-[#111420]"}`} style={{ boxShadow: isOpen ? "0 0 24px rgba(13,148,136,0.1)" : "none" }}>
                  <button onClick={() => setExpandedBento(isOpen ? null : i)} className="w-full text-left px-5 py-5 flex items-start gap-4 group">
                    <span className="text-2xl mt-0.5 flex-shrink-0">{card.icon}</span>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-1 leading-snug">{card.title}</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{card.teaser}</p>
                    </div>
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full border border-teal-400 dark:border-teal-600/30 flex items-center justify-center transition-transform duration-300 ${isOpen ? "rotate-45 bg-teal-100 dark:bg-teal-600/20" : ""}`}>
                      <Plus size={14} className="text-teal-600 dark:text-teal-500" />
                    </div>
                  </button>
                  <div className="overflow-hidden transition-all duration-350 ease-in-out" style={{ maxHeight: isOpen ? "600px" : "0px" }}>
                    <div className="px-5 pb-6 pt-0 ml-12">
                      <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed space-y-4 mb-5">{card.body}</div>
                      <div className="flex flex-wrap gap-2">{card.pills.map((pill, j) => (
                        <Link key={j} to={pill.to} className="inline-flex items-center gap-1 text-xs font-medium rounded-md px-3 py-1.5 transition-colors bg-teal-50 dark:bg-[rgba(13,148,136,0.15)] text-teal-700 dark:text-[#14b8a6] border border-teal-200 dark:border-[rgba(13,148,136,0.25)] hover:bg-teal-100 dark:hover:bg-[rgba(13,148,136,0.25)]">
                          {pill.label} <ArrowRight size={10} />
                        </Link>
                      ))}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════ RELATED RESOURCES ══════════════ */}
      <section className="max-w-5xl mx-auto px-4 pb-12">
        <div className="pt-6 border-t border-gray-200 dark:border-slate-700/30">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white mb-3">Explore More</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            Dive deeper with our <Link to="/futures-prop-firm-comparison" className="text-blue-500 hover:underline">full comparison table</Link> covering every firm's rules, fees, and payouts. Looking to save? Browse the <Link to="/cheapest-futures-prop-firms" className="text-blue-500 hover:underline">cheapest futures prop firms</Link> or find the <Link to="/prop-firms-fast-payouts" className="text-blue-500 hover:underline">fastest payout structures</Link>. New to prop trading? Start with our <Link to="/best-futures-prop-firms-for-beginners" className="text-blue-500 hover:underline">beginner's guide</Link>, then model your risk with the free <Link to="/tools" className="text-blue-500 hover:underline">drawdown calculator</Link>.
          </p>
        </div>
      </section>

      {/* ══════════════ G) FOOTER ══════════════ */}
      <Footer />
    </div>
  );
};

export default HomePage;
